/**
 * JobQuickActions — menu de ações rápidas para um job da agenda.
 *
 * Clique no card do job abre este popover com:
 *  - Ver detalhes (link para /jobs/:id)
 *  - Mover (escolhe nova data/hora)
 *  - Cancelar esta semana (status=cancelada, mantém recorrência)
 *  - Cancelar próximos (cancela este + todos os futuros do mesmo cliente/equipe)
 *  - Excluir (DELETE definitivo, apenas admin)
 *
 * Jobs cancelados/finalizados/em andamento: opções limitadas para evitar erros.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ExternalLink,
  CalendarClock,
  Ban,
  Trash2,
  X,
  Loader2,
  CalendarX,
  UserCog,
  Undo2,
  CalendarCheck,
  ArrowRightLeft,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useConfirm } from "../hooks/useConfirm";
import { confirmOffDay } from "../hooks/useTeamWorkdays";
import { useAuth } from "../auth/AuthContext";

// Converte "HH:MM" (24h) em partes de 12h para os seletores.
function parse12(hhmm) {
  const [H, M] = (hhmm || "09:00").split(":").map((n) => parseInt(n, 10) || 0);
  const ampm = H >= 12 ? "PM" : "AM";
  let h12 = H % 12;
  if (h12 === 0) h12 = 12;
  return { h12: String(h12), min: String(M).padStart(2, "0"), ampm };
}

const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1));
const MINUTES_5 = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
// Atalhos de horários comuns de início (h12, min, ampm).
const TIME_PRESETS = [
  ["7", "00", "AM"], ["8", "00", "AM"], ["9", "00", "AM"], ["10", "00", "AM"],
  ["1", "00", "PM"], ["2", "00", "PM"], ["3", "00", "PM"], ["4", "00", "PM"],
];

export function JobQuickActions({ job, anchorRef, onClose, onMutated, teams = [] }) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [busy, setBusy] = useState(false);
  const [movePicker, setMovePicker] = useState(false);
  const [teamMove, setTeamMove] = useState(false);
  const [moveTarget, setMoveTarget] = useState("");
  const [moveScope, setMoveScope] = useState("permanent");
  const [moveDay, setMoveDay] = useState(
    job?.scheduled_at ? job.scheduled_at.slice(0, 10) : "",
  );
  // Data/hora do "Mover esta data" — hora dividida em seletores fáceis.
  const _initTime = parse12(job?.scheduled_at ? job.scheduled_at.slice(11, 16) : "09:00");
  const [moveDate, setMoveDate] = useState(
    job?.scheduled_at ? job.scheduled_at.slice(0, 10) : "",
  );
  const [moveH12, setMoveH12] = useState(_initTime.h12);
  const [moveMin, setMoveMin] = useState(_initTime.min);
  const [moveAmPm, setMoveAmPm] = useState(_initTime.ampm);

  const isFinal = ["finalizada", "cancelada"].includes(job?.status);
  const isAdmin = user?.role === "admin";
  const [futureCount, setFutureCount] = useState(null);

  // Prévia de quantos atendimentos "Cancelar próximos" cancelaria.
  useEffect(() => {
    if (isFinal || !job?.id) return;
    let alive = true;
    api.get(`/jobs/${job.id}/cancel-future/count`)
      .then(({ data }) => { if (alive) setFutureCount(data.count); })
      .catch(() => {});
    return () => { alive = false; };
  }, [job?.id, isFinal]);

  // Posiciona perto do anchor
  useEffect(() => {
    if (!anchorRef?.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const top = Math.max(8, Math.min(window.innerHeight - 420, r.bottom + 6));
    const left = Math.min(window.innerWidth - 296, Math.max(8, r.left));
    setPos({ top, left });
  }, [anchorRef]);

  // Click outside
  useEffect(() => {
    const onDown = (e) => {
      // Ignora cliques dentro de diálogos de confirmação abertos a partir do painel.
      if (e.target.closest?.('[role="alertdialog"], [role="dialog"]')) return;
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [onClose]);

  async function doCancelWeek() {
    const ok = await confirm({
      title: "Pular esta semana?",
      description:
        "O atendimento desta data será cancelado, mas o contrato continua normalmente — as próximas datas permanecem.",
      actionLabel: "Pular esta semana",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.patch(`/jobs/${job.id}`, { status: "cancelada" });
      toast.success("Semana pausada — contrato segue normal");
      onMutated();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setBusy(false);
    }
  }

  async function doCancelFuture() {
    const ok = await confirm({
      title: "Encerrar contrato a partir desta data?",
      description:
        `Esta data e TODAS as futuras serão canceladas${futureCount != null ? ` (${futureCount} atendimento${futureCount === 1 ? "" : "s"})` : ""}. As já concluídas ficam no histórico.`,
      destructive: true,
      actionLabel: futureCount != null ? `Cancelar ${futureCount} próximos` : "Cancelar próximos",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/cancel-future`);
      toast.success(`${data.cancelled_count} atendimentos cancelados`);
      onMutated();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setBusy(false);
    }
  }

  async function doTeamMove() {
    if (!moveTarget) {
      toast.error("Escolha a equipe de destino");
      return;
    }
    if (moveScope === "day" && !moveDay) {
      toast.error("Escolha o dia");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/teams/move-client", {
        client_id: job.client_id,
        target_team_id: moveTarget,
        scope: moveScope,
        day: moveScope === "day" ? moveDay : undefined,
      });
      if (data.scope === "permanent") {
        toast.success("Casa movida definitivamente para a nova equipe");
      } else {
        const rd = data.return_date
          ? new Date(data.return_date + "T00:00:00").toLocaleDateString("pt-BR")
          : "";
        toast.success(
          `Casa movida (${data.moved_jobs} atend.) — volta à equipe original em ${rd}`,
        );
      }
      onMutated();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao mover");
    } finally {
      setBusy(false);
    }
  }

  async function doMove() {
    if (!moveDate) {
      toast.error("Escolha a nova data");
      return;
    }
    // Monta hora local (wall-clock) 'YYYY-MM-DDTHH:MM:SS' — sem fuso, para o
    // backend gravar exatamente o horário escolhido (_wallclock_iso).
    let H = parseInt(moveH12, 10) % 12;
    if (moveAmPm === "PM") H += 12;
    const hh = String(H).padStart(2, "0");
    const localIso = `${moveDate}T${hh}:${moveMin}:00`;
    const teamName = job.team?.name || teams.find((t) => t.id === job.team_id)?.name;
    if (!(await confirmOffDay(confirm, job.team_id, localIso, teamName))) return;
    setBusy(true);
    try {
      await api.patch(`/jobs/${job.id}`, { scheduled_at: localIso });
      toast.success("Atendimento movido");
      onMutated();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setBusy(false);
    }
  }

  async function doRevertToScheduled() {
    const ok = await confirm({
      title: "Voltar para Agendada?",
      description:
        "O atendimento volta ao status 'Agendada' e os dados de START (hora/GPS) serão apagados. Útil se a equipe iniciou por engano.",
      actionLabel: "Voltar para agendada",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/revert-to-scheduled`);
      toast.success("Atendimento revertido para agendada");
      onMutated();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setBusy(false);
    }
  }

  async function doReopen() {
    const ok = await confirm({
      title: "Reabrir casa finalizada?",
      description:
        "A casa volta para 'Agendada'. Os dados de finalização (fotos, assinatura, hora de término) serão APAGADOS e o item de 'A Receber' gerado por ela será removido (se ainda não foi faturado).",
      actionLabel: "Reabrir",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/reopen`);
      toast.success(
        data?.invoice_item_removed
          ? "Casa reaberta — voltou para agendada e item de A Receber removido"
          : "Casa reaberta — voltou para agendada",
      );
      onMutated();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setBusy(false);
    }
  }

  async function doReactivate() {
    const ok = await confirm({
      title: "Reativar esta casa?",
      description:
        "O atendimento volta para o status 'Agendada' e aparece novamente na agenda dos colaboradores.",
      actionLabel: "Reativar",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.patch(`/jobs/${job.id}`, { status: "agendada" });
      toast.success("Casa reativada — voltou para agendada");
      onMutated();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    const ok = await confirm({
      title: "Excluir definitivamente?",
      description: "Este dia e TODOS os próximos desta casa nesta equipe serão apagados do sistema. Casas já finalizadas ficam no histórico.",
      destructive: true,
      actionLabel: "Excluir tudo",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/jobs/${job.id}`);
      toast.success(`${data?.deleted_count ?? 1} atendimento(s) excluído(s)`);
      onMutated();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      ref={ref}
      data-testid="job-quick-actions"
      className="fixed z-50 w-[280px] max-h-[80vh] overflow-y-auto bg-white rounded-xl shadow-2xl border border-slate-200"
      style={{ top: pos.top, left: pos.left }}
    >
      <div className="flex items-start justify-between gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Ações rápidas
          </div>
          <div className="text-xs text-slate-700 font-semibold truncate">
            {job.client_name || "Cliente"} ·{" "}
            {new Date(job.scheduled_at).toLocaleString("en-US", {
              day: "2-digit",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-700 p-1"
        >
          <X size={14} />
        </button>
      </div>

      {movePicker ? (
        <div className="p-3 space-y-3" data-testid="job-move-panel">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Nova data
            </label>
            <input
              type="date"
              value={moveDate}
              onChange={(e) => setMoveDate(e.target.value)}
              data-testid="job-move-date"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Horário
            </label>
            <div className="grid grid-cols-3 gap-1.5">
              <select
                value={moveH12}
                onChange={(e) => setMoveH12(e.target.value)}
                data-testid="job-move-hour"
                className="px-2 py-2 text-sm text-center border border-slate-200 rounded-md focus:border-blue-500 focus:outline-none bg-white"
                aria-label="Hora"
              >
                {HOURS_12.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
              <select
                value={moveMin}
                onChange={(e) => setMoveMin(e.target.value)}
                data-testid="job-move-minute"
                className="px-2 py-2 text-sm text-center border border-slate-200 rounded-md focus:border-blue-500 focus:outline-none bg-white"
                aria-label="Minuto"
              >
                {MINUTES_5.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-md p-0.5">
                {["AM", "PM"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setMoveAmPm(p)}
                    data-testid={`job-move-ampm-${p.toLowerCase()}`}
                    className={`text-xs font-bold rounded ${
                      moveAmPm === p ? "bg-blue-600 text-white" : "text-slate-600"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
              Atalhos
            </div>
            <div className="flex flex-wrap gap-1" data-testid="job-move-presets">
              {TIME_PRESETS.map(([h, m, ap]) => {
                const active = moveH12 === h && moveMin === m && moveAmPm === ap;
                return (
                  <button
                    key={`${h}-${m}-${ap}`}
                    type="button"
                    onClick={() => { setMoveH12(h); setMoveMin(m); setMoveAmPm(ap); }}
                    data-testid={`job-move-preset-${h}${ap.toLowerCase()}`}
                    className={`px-2 py-1 rounded-full text-[11px] font-semibold border ${
                      active
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {h}:{m} {ap}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex gap-1.5 pt-1">
            <button
              onClick={() => setMovePicker(false)}
              disabled={busy}
              className="flex-1 text-xs font-semibold px-2 py-2 rounded-md bg-slate-100 hover:bg-slate-200"
            >
              Voltar
            </button>
            <button
              onClick={doMove}
              disabled={busy}
              data-testid="job-move-confirm"
              className="flex-1 text-xs font-semibold px-2 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center justify-center gap-1"
            >
              {busy && <Loader2 size={12} className="animate-spin" />}
              Confirmar
            </button>
          </div>
        </div>
      ) : teamMove ? (
        <div className="p-3 space-y-2" data-testid="job-team-move-panel">
          <label className="block text-xs font-semibold text-slate-600">
            Mover para a equipe
          </label>
          <select
            value={moveTarget}
            onChange={(e) => setMoveTarget(e.target.value)}
            data-testid="job-team-move-target"
            className="w-full px-2 py-2 text-sm border border-slate-200 rounded-md focus:border-blue-500 focus:outline-none bg-white"
          >
            <option value="">Selecione…</option>
            {teams
              .filter((t) => t.id !== job.team_id)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </select>
          <label className="block text-xs font-semibold text-slate-600 pt-1">
            Por quanto tempo
          </label>
          <div className="space-y-1">
            {[
              ["day", "Somente um dia"],
              ["week", "Uma semana (7 dias)"],
              ["month", "Um mês (30 dias)"],
              ["permanent", "Definitivamente"],
            ].map(([val, lbl]) => (
              <label
                key={val}
                className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer"
              >
                <input
                  type="radio"
                  name="move-scope"
                  checked={moveScope === val}
                  onChange={() => setMoveScope(val)}
                  data-testid={`job-team-move-scope-${val}`}
                />
                {lbl}
              </label>
            ))}
          </div>
          {moveScope === "day" && (
            <input
              type="date"
              value={moveDay}
              onChange={(e) => setMoveDay(e.target.value)}
              data-testid="job-team-move-day"
              className="w-full px-2 py-2 text-sm border border-slate-200 rounded-md focus:border-blue-500 focus:outline-none"
            />
          )}
          <div className="flex gap-1.5 pt-1">
            <button
              onClick={() => setTeamMove(false)}
              disabled={busy}
              className="flex-1 text-xs font-semibold px-2 py-2 rounded-md bg-slate-100 hover:bg-slate-200"
            >
              Voltar
            </button>
            <button
              onClick={doTeamMove}
              disabled={busy}
              data-testid="job-team-move-confirm"
              className="flex-1 text-xs font-semibold px-2 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center justify-center gap-1"
            >
              {busy && <Loader2 size={12} className="animate-spin" />}
              Mover
            </button>
          </div>
        </div>
      ) : (
        <div className="py-1">
          <Action
            as={Link}
            to={`/jobs/${job.id}`}
            icon={ExternalLink}
            label="Ver detalhes"
            testId="job-action-detail"
            onClick={onClose}
          />
          {job.client_id && (
            <Action
              as={Link}
              to={`/clients?edit=${job.client_id}`}
              icon={UserCog}
              label="Editar cliente"
              testId="job-action-edit-client"
              onClick={onClose}
            />
          )}
          {!isFinal && (
            <Action
              icon={CalendarClock}
              label="Mover esta data"
              testId="job-action-move"
              onClick={() => setMovePicker(true)}
            />
          )}
          {!isFinal && job.client_id && teams.length > 1 && (
            <Action
              icon={ArrowRightLeft}
              label="Mover para outra equipe"
              testId="job-action-team-move"
              onClick={() => setTeamMove(true)}
            />
          )}
          {!isFinal && (
            <Action
              icon={Ban}
              label="Pular só esta semana"
              testId="job-action-cancel-week"
              onClick={doCancelWeek}
              tone="amber"
              busy={busy}
            />
          )}
          {!isFinal && (
            <Action
              icon={CalendarX}
              label={`Cancelar próximos${futureCount != null ? ` (${futureCount})` : ""}`}
              testId="job-action-cancel-future"
              onClick={doCancelFuture}
              tone="red"
              busy={busy}
            />
          )}
          {isAdmin && job?.status === "andamento" && (
            <Action
              icon={Undo2}
              label="Voltar para agendada"
              testId="job-action-revert"
              onClick={doRevertToScheduled}
              tone="amber"
              busy={busy}
            />
          )}
          {(isAdmin || user?.role === "supervisor") && job?.status === "cancelada" && (
            <Action
              icon={CalendarCheck}
              label="Reativar casa"
              testId="job-action-reactivate"
              onClick={doReactivate}
              tone="green"
              busy={busy}
            />
          )}
          {isAdmin && job?.status === "finalizada" && (
            <Action
              icon={Undo2}
              label="Reabrir casa finalizada"
              testId="job-action-reopen"
              onClick={doReopen}
              tone="amber"
              busy={busy}
            />
          )}
          {isAdmin && (
            <Action
              icon={Trash2}
              label="Excluir definitivamente"
              testId="job-action-delete"
              onClick={doDelete}
              tone="red"
              busy={busy}
            />
          )}
        </div>
      )}
    </div>
  );
}

function Action({ as: As = "button", icon: Icon, label, onClick, testId, tone, busy, to }) {
  const cls =
    tone === "red"
      ? "text-red-600 hover:bg-red-50"
      : tone === "amber"
        ? "text-amber-700 hover:bg-amber-50"
        : tone === "green"
          ? "text-emerald-700 hover:bg-emerald-50"
          : "text-slate-700 hover:bg-slate-50";
  const props =
    As === Link ? { to, onClick } : { onClick, disabled: busy, type: "button" };
  return (
    <As
      {...props}
      data-testid={testId}
      className={`w-full text-left text-sm px-3 py-2 flex items-center gap-2 ${cls} disabled:opacity-50`}
    >
      <Icon size={14} className="flex-shrink-0" />
      <span className="truncate">{label}</span>
    </As>
  );
}
